import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISidebarFilterOptionsContext } from '../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { CustomerVenFilterKeys, TCustomerVenFilterKey } from './CustomerVenSidebarFilters';
import { ISavedFilter } from '../../../components/sidebar-filters/saved-filter/interface';

export const transformCustomerVenSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TCustomerVenFilterKey, string> => {
    let result: Record<TCustomerVenFilterKey, string> = {} as Record<TCustomerVenFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case CustomerVenFilterKeys.controlProviderId:
                result[key] = value
                    .map((val: string) => {
                        const option = context.controlProviderOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case CustomerVenFilterKeys.type:
                const typeOption = context.venTypeOptions.find(option => option.value === value);
                result[key] = typeOption ? typeOption.label : 'unknown';
                break;
            case CustomerVenFilterKeys.status:
                const statusOption = context.venStatusOptions.find(option => option.value === value);
                result[key] = statusOption ? statusOption.label : 'unknown';
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TCustomerVenFilterKey, string>;
    }

    return result;
};

export const transformCustomerVenSavedFilterValuesFromDto = (filter: ISavedFilter['filter_values']): Record<TCustomerVenFilterKey, unknown> => {
    const result: Record<TCustomerVenFilterKey, unknown> = {} as Record<TCustomerVenFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case CustomerVenFilterKeys.controlProviderId:
            case CustomerVenFilterKeys.type:
            case CustomerVenFilterKeys.status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformCustomerVenSavedFilterValuesToDto = (filter: ISavedFilter['filter_values']): Record<TCustomerVenFilterKey, unknown> => {
    const result: Record<TCustomerVenFilterKey, unknown> = {} as Record<TCustomerVenFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case CustomerVenFilterKeys.controlProviderId:
            case CustomerVenFilterKeys.type:
            case CustomerVenFilterKeys.status:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case CustomerVenFilterKeys.controlProviderId:
                if (Array.isArray(value)) {
                    const validOptionValues = context.controlProviderOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case CustomerVenFilterKeys.type:
                if (typeof value === 'string' && !context.venTypeOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case CustomerVenFilterKeys.status:
                if (typeof value === 'string' && !context.venStatusOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TCustomerVenFilterKey, string>): TCustomerVenFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TCustomerVenFilterKey[];
}
